import { SimpleGrid, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { StatBoxV2 } from '@feathr/components';
import { currencyFormatter } from '@feathr/extender/utils';

import type { TConversionKind } from '../../ReportsConversionsGoalManagerPage.hooks';
import type { IDelta } from './GoalDetailStats.utils';
import { deltaProps } from './GoalDetailStats.utils';

export interface IGoalDetailStatsProps {
  conversionsDelta: IDelta;
  kind: TConversionKind;
  primaryOnCount: number;
  trackedOnCount: number;
  totalConversions: number;
  totalConversionValue: number;
  uniqueConverters: number;
  uniqueConvertersDelta: IDelta;
  valueDelta: IDelta;
}

function GoalDetailStats({
  conversionsDelta,
  kind,
  primaryOnCount,
  trackedOnCount,
  totalConversions,
  totalConversionValue,
  uniqueConverters,
  uniqueConvertersDelta,
  valueDelta,
}: Readonly<IGoalDetailStatsProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const isCampaign = kind === 'campaign';
  const comparedLabel = t('Compared to last 30d');

  return (
    <SimpleGrid cols={{ base: 1, md: 2, lg: 4 }} spacing={theme.spacing['4']}>
      <StatBoxV2
        {...deltaProps(conversionsDelta, comparedLabel)}
        label={isCampaign ? t('Total campaign conversions') : t('Total flight conversions')}
        name={'total-conversions'}
        primary={totalConversions.toLocaleString('en-US')}
        tooltip={t('Lifetime conversions recorded for this goal')}
      />
      <StatBoxV2
        {...deltaProps(valueDelta, comparedLabel)}
        label={t('Total value')}
        name={'total-conversion-value'}
        primary={currencyFormatter.format(totalConversionValue)}
        tooltip={t('Lifetime conversion value recorded for this goal')}
      />
      <StatBoxV2
        {...deltaProps(uniqueConvertersDelta, comparedLabel)}
        label={t('Unique converters')}
        name={'unique-converters'}
        primary={uniqueConverters.toLocaleString('en-US')}
        tooltip={t('Unique people who have converted on this goal')}
      />
      <StatBoxV2
        description={t('Primary on {{count}}', { count: primaryOnCount })}
        label={t('Tracked on')}
        name={'tracked-on'}
        primary={
          isCampaign
            ? t('{{count}} campaigns', { count: trackedOnCount })
            : t('{{count}} flights', { count: trackedOnCount })
        }
        tooltip={isCampaign ? t('Campaigns tracking this goal') : t('Flights tracking this goal')}
      />
    </SimpleGrid>
  );
}

export default observer(GoalDetailStats);
